import { css } from "@emotion/react";
import { Theme, useTheme } from "@mui/material";
import Link from "next/link";
import { useState } from "react";
import { match } from "ts-pattern";

import SingleSelectInput from "~/components/common/SingleSelectInput";
import FussyPointChart from "~/components/me/FussyPointChart";
import CategoryPointPanel from "~/components/me/bento/content/point-report/CategoryPointPanel";
import { totalPoint } from "~/components/me/bento/content/point-report/lib";
import {
  flexRow,
  mediaQueryIsDesktop,
  mediaQueryIsMobile,
  wrapLongTextStyle,
} from "~/lib/client/design";
import { MePointReportQuery } from "~/lib/graphql/client";
import "~/lib/monkeypatch";

const DialogBody = ({ content }: { content: MePointReportQuery }) => {
  const theme = useTheme();

  const [selectedDataRange, setSelectedDataRange] = useState<"DATE" | "MONTH">(
    "DATE"
  );
  const selectDate = () => setSelectedDataRange("DATE");
  const selectMonth = () => setSelectedDataRange("MONTH");

  const monthlyData = content.me.fussyPointsByMonth.map(
    (fussyPointByMonth) => ({
      yearAndMonth: fussyPointByMonth.yearAndMonth,
      fussyPoint: totalPoint(fussyPointByMonth.point),
      category: {
        id: fussyPointByMonth.point.category.id,
        name: fussyPointByMonth.point.category.name,
      },
    })
  );

  const dailyData = content.me.fussyPointsByDate.map((fussyPointByDate) => ({
    date: fussyPointByDate.date,
    fussyPoint: totalPoint(fussyPointByDate.point),
    category: {
      id: fussyPointByDate.point.category.id,
      name: fussyPointByDate.point.category.name,
    },
  }));

  const totalData = [...content.me.fussyPoints].sort(
    (a, b) => totalPoint(b) - totalPoint(a)
  );

  return (
    <div css={rootStyle}>
      <h2>最近の活動レポート</h2>

      <div css={pointChartContainerStyle}>
        {match(selectedDataRange)
          .with("MONTH", () => <FussyPointChart data={monthlyData} />)
          .with("DATE", () => <FussyPointChart data={dailyData} />)
          .exhaustive()}
      </div>
      <div css={selectorContainerStyle}>
        <SingleSelectInput
          label={"日次"}
          selected={selectedDataRange == "DATE"}
          setSelected={selectDate}
        />
        <SingleSelectInput
          label={"月次"}
          selected={selectedDataRange == "MONTH"}
          setSelected={selectMonth}
        />
      </div>
      <p css={descriptionStyle(theme)}>
        直近3ヶ月と1年の活動で獲得したポイントを確認できます。
        いつ、どのくらいハマっていたのかなどを見てお楽しみください。
        <Link
          href={
            "https://fussy.gitbook.io/fussy-user-guide/howtocontribute/getpoints/pointonorepto"
          }
          css={linkStyle}
        >
          詳しくはこちら
        </Link>
      </p>
      <h2>累積ポイント</h2>
      <p css={descriptionStyle(theme)}>
        これまでの活動で獲得したポイントの合計が確認できます。
      </p>
      <div css={categoryPointContainerStyle}>
        {totalData.map((fussyPoint, index) => (
          <CategoryPointPanel
            point={fussyPoint}
            ordinalLabel={`${(index + 1).ordinal()}`}
            key={index}
          />
        ))}
      </div>
    </div>
  );
};

const rootStyle = css`
  width: 100%;
  margin-top: 1rem;
`;

const descriptionStyle = (theme: Theme) => css`
  margin: 1rem 0 0;
  font-size: 0.8rem;
  color: ${theme.palette.text.secondary};
  ${wrapLongTextStyle};
`;

const linkStyle = css`
  text-decoration: underline;
  margin-left: 0.125rem;
`;

const selectorContainerStyle = css`
  margin-top: 0.5rem;
  ${flexRow};
  gap: 4px;
`;

const pointChartContainerStyle = css`
  margin-top: 1rem;
  overflow-y: scroll;
  padding: 1rem;
`;

const categoryPointContainerStyle = css`
  padding: 0;
  margin: 2rem auto;

  width: 100%;
  display: grid;
  justify-content: space-around;

  ${mediaQueryIsMobile(css`
    grid-template-columns: 1fr 1fr;
    //grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 8px;
  `)}

  ${mediaQueryIsDesktop(css`
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 10px;
  `)}
`;

/**
 * @package
 */
export default DialogBody;
